import { cn } from "@arcadeai/design-system/lib/utils";
import {
  getMcpClientMeta,
  type McpClientId,
} from "@arcadeai/ui-kit/mcp-gateway-connect";
import type { JSX, ReactNode } from "react";
import type { Jtbd } from "@/app/onboarding-first-win/api/get-jtbds";
import { OnboardingConnectDiagram } from "@/app/onboarding-first-win/components/onboarding-connect-diagram";
import { OnboardingPromptCtas } from "@/app/onboarding-first-win/components/onboarding-prompt-ctas";

type FirstCallSectionBodyProps = {
  clientForNav?: McpClientId;
  hasExecutedExternally: boolean;
  jtbd: Jtbd | null;
  onPromptCopy: () => void;
  onPromptDeepLink: () => void;
  services: string[];
};

function firstCallClientLabel(
  clientForNav: McpClientId | undefined,
  clientMeta: ReturnType<typeof getMcpClientMeta> | null
): string {
  if (!clientForNav || clientForNav === "mcp_url") {
    return "your MCP client";
  }
  return clientMeta?.name ?? "your MCP client";
}

function firstCallIntroCopy(
  examplePrompt: string | null,
  jtbd: Jtbd | null,
  clientPhrase: string
): string {
  if (examplePrompt) {
    return `Send a message in ${clientPhrase} to put your tools to use.`;
  }

  if (!jtbd) {
    return `In ${clientPhrase}, run any tool your gateway exposes — for example, list messages or search records. We'll detect the first call here.`;
  }

  return `In ${clientPhrase}, run any tool from your gateway and we'll detect it here.`;
}

export function FirstCallSectionBody({
  jtbd,
  clientForNav,
  hasExecutedExternally,
  onPromptCopy,
  onPromptDeepLink,
  services,
}: FirstCallSectionBodyProps): JSX.Element {
  const examplePrompt = jtbd?.example_prompts?.[0] ?? null;
  const clientMeta = clientForNav ? getMcpClientMeta(clientForNav) : null;
  const clientPhrase = firstCallClientLabel(clientForNav, clientMeta);

  let promptSection: ReactNode = null;
  if (clientForNav === "mcp_url") {
    promptSection = (
      <p className="text-muted-foreground text-sm leading-relaxed">
        Open the MCP-compatible client where you connected the URL and send a
        first message there.
      </p>
    );
  } else if (examplePrompt && clientForNav) {
    promptSection = (
      <OnboardingPromptCtas
        clientId={clientForNav}
        onPromptCopy={onPromptCopy}
        onPromptDeepLink={onPromptDeepLink}
        prompt={examplePrompt}
      />
    );
  }

  return (
    <section className="space-y-6 pb-2">
      <p className="text-muted-foreground text-sm leading-relaxed">
        {firstCallIntroCopy(examplePrompt, jtbd, clientPhrase)}
      </p>

      <div
        className={cn(
          "flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed py-4 text-center transition-colors md:py-8",
          hasExecutedExternally
            ? "border-primary/30 bg-primary/5"
            : "border-border bg-muted/20"
        )}
      >
        <OnboardingConnectDiagram clientId={clientForNav} services={services} />
        <p
          className={cn(
            "text-muted-foreground text-xs",
            hasExecutedExternally && "font-medium text-foreground"
          )}
        >
          {hasExecutedExternally
            ? "First tool call detected"
            : "Waiting for your first tool call…"}
        </p>
      </div>

      {promptSection}
    </section>
  );
}
